import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { api, apiError, tanggal } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ username: "", password: "", role: "admin", nama_panggilan: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", {
        username: form.username,
        password: form.password,
        role: form.role,
        nama_panggilan: form.role === "anggota" ? form.nama_panggilan : null,
      });
      toast.success("Pengguna berhasil dibuat");
      setOpen(false);
      setForm({ username: "", password: "", role: "admin", nama_panggilan: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Password minimal 6 karakter");
    try {
      await api.put(`/users/${pwOpen.id}`, { password: newPw });
      toast.success(`Password ${pwOpen.username} diperbarui`);
      setPwOpen(null);
      setNewPw("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus pengguna ${row.username}?`)) return;
    try {
      await api.delete(`/users/${row.id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Manajemen Pengguna"
        title="Hak Akses & Akun"
        description="Owner/Admin dapat mengakses seluruh menu. Anggota hanya melihat Data IPO dan dashboard miliknya."
      >
        <Button
          data-testid="user-add-btn"
          onClick={() => setOpen(true)}
          className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
        >
          <Plus size={16} className="mr-1.5" /> Tambah Pengguna
        </Button>
      </PageHeader>

      <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#2b3550]" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="Belum ada pengguna" />
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="users-table" className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-[#1e2536] bg-[#0d1119] text-left text-xs uppercase tracking-wider text-[#4d586f]">
                  <th className="px-4 py-3 font-medium">Username</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Terhubung Anggota</th>
                  <th className="px-4 py-3 font-medium">Dibuat</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`user-row-${r.username}`} className="border-b border-[#1a2030] last:border-0 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 text-white">{r.username}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs uppercase tracking-wider ${
                          r.role === "anggota"
                            ? "bg-[#8f9bb3]/10 text-[#8f9bb3]"
                            : "bg-[#00d09c]/10 text-[#00d09c]"
                        }`}
                      >
                        {r.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#8f9bb3]">{r.nama_panggilan || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{tanggal(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`user-reset-pw-btn-${r.username}`}
                          size="icon"
                          variant="outline"
                          onClick={() => setPwOpen(r)}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#00d09c]"
                        >
                          <KeyRound size={14} />
                        </Button>
                        <Button
                          data-testid={`user-delete-btn-${r.username}`}
                          size="icon"
                          variant="outline"
                          onClick={() => remove(r)}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#ff3b30]"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog" className="border-[#1e2536] bg-[#10141d]">
          <DialogHeader>
            <DialogTitle className="text-white">Tambah Pengguna Baru</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Username</Label>
              <Input
                data-testid="user-input-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Password</Label>
              <Input
                data-testid="user-input-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-select-role" className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[#1e2536] bg-[#10141d] text-white">
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="anggota">Anggota</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "anggota" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">
                  Nama Panggilan Anggota (relasi)
                </Label>
                <Input
                  data-testid="user-input-nickname"
                  value={form.nama_panggilan}
                  onChange={(e) => setForm({ ...form, nama_panggilan: e.target.value })}
                  className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#1e2536] bg-transparent text-[#8f9bb3]">
              Batal
            </Button>
            <Button
              data-testid="user-save-btn"
              onClick={create}
              disabled={saving}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(v) => !v && setPwOpen(null)}>
        <DialogContent data-testid="user-pw-dialog" className="border-[#1e2536] bg-[#10141d]">
          <DialogHeader>
            <DialogTitle className="text-white">Reset Password {pwOpen?.username}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="user-new-password-input"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="Password baru"
            className="border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
          />
          <DialogFooter>
            <Button
              data-testid="user-pw-save-btn"
              onClick={resetPw}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              Simpan Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
